import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import {
  CreateVoucherDeliveryOptionRequest,
  VoucherDeliveryOptionApi as DevliveryOptionsApiTs,
  ShowVoucherDeliveryOptionResponse,
  UpdateVoucherDeliveryOptionRequest,
  ListVoucherDeliveryOptionsResponse,
  ResponseError,
  ListVoucherDeliveryOptionsRequest,
  ListOrderedVoucherDeliveryOptionsResponse,
  ListSortedVoucherDeliveryOptionsRequest,
} from '@trybeapp/sdk'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const api = new DevliveryOptionsApiTs(apiConfiguration)

export const usePagedVoucherDeliveryOptions = (
  params: ListVoucherDeliveryOptionsRequest = {},
  options: UseQueryOptions<ListVoucherDeliveryOptionsResponse, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListVoucherDeliveryOptionsResponse, ResponseError>(
    ['voucherDeliveryOptions', siteId, params],
    () => api.listVoucherDeliveryOptions({ siteId, ...params }),
    options
  )
}

export const useCreateVoucherDeliveryOption = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<
    ShowVoucherDeliveryOptionResponse,
    ResponseError,
    CreateVoucherDeliveryOptionRequest
  >(
    (voucherDeliveryOption) =>
      api.createVoucherDeliveryOption({
        createVoucherDeliveryOptionRequest: {
          ...voucherDeliveryOption,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherDeliveryOptions'])
      },
    }
  )
}

export const useUpdateVoucherDeliveryOption = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ShowVoucherDeliveryOptionResponse,
    ResponseError,
    [string, UpdateVoucherDeliveryOptionRequest]
  >(
    ([voucherDeliveryOptionId, voucherDeliveryOption]) =>
      api.updateVoucherDeliveryOption({
        voucherDeliveryOptionId,
        updateVoucherDeliveryOptionRequest: voucherDeliveryOption,
      }),
    {
      onSuccess: (_, [voucherDeliveryOptionId]) => {
        queryClient.invalidateQueries(['voucherDeliveryOptions'])
      },
    }
  )
}

export const useUpdateVoucherDeliveryOptionOrder = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ListOrderedVoucherDeliveryOptionsResponse, ResponseError, string[]>(
    (voucherDeliveryOptionIds) =>
      api.updateVoucherDeliveryOptionOrder({
        updateVoucherDeliveryOptionOrderRequest: {
          voucher_delivery_option_ids: voucherDeliveryOptionIds,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherDeliveryOptions'])
      },
    }
  )
}

export const useOrderedVoucherDeliveryOptions = (
  params: ListSortedVoucherDeliveryOptionsRequest = {},
  options: UseQueryOptions<ListOrderedVoucherDeliveryOptionsResponse, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListOrderedVoucherDeliveryOptionsResponse, ResponseError>(
    ['voucherDeliveryOptions', siteId, params],
    () => api.listSortedVoucherDeliveryOptions({ siteId, ...params }),
    options
  )
}

export const useGetVoucherDeliveryOption = (
  voucherDeliveryOptionId: string,
  options?: UseQueryOptions<ShowVoucherDeliveryOptionResponse, ResponseError>
) =>
  useQuery<ShowVoucherDeliveryOptionResponse, ResponseError>(
    ['voucherDeliveryOption', { voucherDeliveryOptionId }],
    () => api.getVoucherDeliveryOption({ voucherDeliveryOptionId }),
    options
  )

export const useRestoreVoucherDeliveryOption = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowVoucherDeliveryOptionResponse, ResponseError, string>(
    (voucherDeliveryOptionId) =>
      api.restoreVoucherDeliveryOption({
        voucherDeliveryOptionId: voucherDeliveryOptionId,
      }),
    {
      onSuccess: (_, voucherDeliveryOptionId) => {
        queryClient.invalidateQueries(['voucherDeliveryOptions', voucherDeliveryOptionId])
      },
    }
  )
}

export const useDeleteVoucherDeliveryOption = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, string>(
    (voucherDeliveryOptionId) =>
      api.deleteVoucherDeliveryOption({
        voucherDeliveryOptionId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherDeliveryOptions'])
      },
    }
  )
}
